import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BetSummaryService } from './bet-summary.service';
import { TableBetSummaryComponent } from '../table-bet-summary/table-bet-summary.component';
import { saveAs } from 'file-saver';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-bet-summary',
  templateUrl: './bet-summary.component.html',
  styleUrls: ['./bet-summary.component.scss'],
})
export class BetSummaryComponent {
  totalBet: number = 0;
  totalLoss: number = 0;
  totalAwards: number = 0;
  rtp: number = 0;
  balanceGGR: number = 0;
  debtorGGR: number = 0;

  startRaw = '';
  endRaw = '';

  filters = new FormGroup({
    filterType: new FormControl<string>('accountId'),
    searchValue: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    endDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
  });

  filterTypeOptions = [
    { label: 'Operador', value: 'accountId' },
    { label: 'Documento de Jogador', value: 'document' },
  ];

  public isFilterColapsed = false;
  public isLoaded = false;
  private dashboardLoaded = false;
  private tableLoaded = false;
  private errorToastShown = false;

  @ViewChild(TableBetSummaryComponent, { static: true })
  table!: TableBetSummaryComponent;

  constructor(
    private betSummaryService: BetSummaryService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    this.filters.controls.initialDate.setValue(today);
    this.filters.controls.endDate.setValue(today);

    this.startRaw = this.formatBr(today);
    this.endRaw = this.formatBr(today);

    this.loadScreen();
  }

  onRowsLoaded(): void {
    this.tableLoaded = true;
    this.checkLoaded();
  }

  onTableLoadError(): void {
    this.tableLoaded = true;
    this.showLoadError();
    this.checkLoaded();
  }

  private checkLoaded(): void {
    this.isLoaded = this.dashboardLoaded && this.tableLoaded;
  }

  private showLoadError(): void {
    if (this.errorToastShown) return;

    this.errorToastShown = true;
    this.toastr.error('Erro ao carregar resumo de apostas.', 'Erro', {
      timeOut: 3000,
      positionClass: 'toast-top-right',
    });
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private formatBr(d: Date): string {
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private toIsoStart(d: Date): string {
    return `${this.ymd(d)}T00:00:00Z`;
  }

  private toIsoEnd(d: Date): string {
    return `${this.ymd(d)}T23:59:59Z`;
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;

    const day = +m[1];
    const month = +m[2] - 1;
    const year = +m[3];

    const d = new Date(year, month, day, 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string): Date | null {
    const ctrl = this.filters.controls[ctrlName];

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
      return parsed;
    }

    if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }

    return null;
  }

  onDateChange(ctrlName: 'initialDate' | 'endDate', raw: string): void {
    const parsed = this.parseDateInput(ctrlName, raw);
    if (!parsed) return;

    const normalized = this.formatBr(parsed);
    if (ctrlName === 'initialDate') this.startRaw = normalized;
    else this.endRaw = normalized;
  }

  getSearchPlaceholder(): string {
    const type = this.filters.controls.filterType.value || 'accountId';

    if (type === 'accountId') return 'Ex: 43418e07-b390-4ece-ab01-2e8b334c5b59';
    return 'Ex: 123.456.789-00';
  }

  async pasteSearchValue(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.filters.controls.searchValue.setValue(text.trim());
    } catch {
      this.toastr.error('Erro ao colar conteúdo.', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    }
  }

  private sanitizeDocument(value: string | null | undefined): string {
    return (value || '').replace(/\D/g, '');
  }

  private getSanitizedSearchValue(): string {
    const filterType = this.filters.controls.filterType.value || 'accountId';
    let value = (this.filters.controls.searchValue.value || '').trim();

    if (filterType === 'document') {
      value = this.sanitizeDocument(value);
    }

    return value;
  }

  private buildParams() {
    const start = this.filters.controls.initialDate.value as Date | null;
    const end = this.filters.controls.endDate.value as Date | null;
    const filterType = this.filters.controls.filterType.value || 'accountId';
    const searchValue = this.getSanitizedSearchValue();

    const params: any = {
      startDate: this.toIsoStart(this.asDate(start!)),
      endDate: this.toIsoEnd(this.asDate(end!)),
    };

    if (searchValue) {
      if (filterType === 'accountId') params.accountId = searchValue;
      if (filterType === 'document') params.document = searchValue;
    }

    return params;
  }

  private loadDashboard(): void {
    const params = this.buildParams();

    this.betSummaryService.getSummaryBetMonthly(params).subscribe({
      next: (value: any) => {
        const data =
          value?.summaryBet ??
          value?.summaryBetMontly ??
          value?.summaryBetMonthly ??
          value?.data ??
          value?.result ??
          value ??
          {};

        this.totalBet = Number(data?.totalBet ?? 0);
        this.totalLoss = Number(data?.totalLoss ?? 0);
        this.totalAwards = Number(data?.totalAwards ?? 0);
        this.rtp = Number(data?.rtp ?? 0);
        this.balanceGGR = Number(data?.balanceGGR ?? 0);
        this.debtorGGR = Number(data?.debtorGGR ?? 0);

        this.dashboardLoaded = true;
        this.checkLoaded();
      },
      error: () => {
        this.totalBet = 0;
        this.totalLoss = 0;
        this.totalAwards = 0;
        this.rtp = 0;
        this.balanceGGR = 0;
        this.debtorGGR = 0;

        this.dashboardLoaded = true;
        this.showLoadError();
        this.checkLoaded();
      },
    });
  }

  loadScreen(): void {
    this.dashboardLoaded = false;
    this.tableLoaded = false;
    this.isLoaded = false;
    this.errorToastShown = false;

    const params = this.buildParams();

    if (!this.table) {
      this.dashboardLoaded = true;
      this.tableLoaded = true;
      this.isLoaded = true;
      this.showLoadError();
      return;
    }

    this.table.page = 1;
    this.table.startDateIso = params.startDate;
    this.table.endDateIso = params.endDate;
    this.table.filterType = this.filters.controls.filterType.value || 'accountId';
    this.table.searchValue = this.getSanitizedSearchValue();
    this.table.load();

    this.loadDashboard();
  }

  buscar(startRaw?: string, endRaw?: string): void {
    if (startRaw !== undefined) this.parseDateInput('initialDate', startRaw);
    if (endRaw !== undefined) this.parseDateInput('endDate', endRaw);

    this.filters.markAllAsTouched();
    this.filters.updateValueAndValidity({ emitEvent: false });

    const start = this.filters.controls.initialDate.value as Date | null;
    const end = this.filters.controls.endDate.value as Date | null;

    if (!start || !end) return;

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    if (this.filters.invalid) return;

    this.loadScreen();
  }

  filterColapse(): void {
    this.isFilterColapsed = !this.isFilterColapsed;
  }

  downloadPdf(): void {
    if (this.startRaw) this.parseDateInput('initialDate', this.startRaw);
    if (this.endRaw) this.parseDateInput('endDate', this.endRaw);

    this.filters.markAllAsTouched();
    this.filters.updateValueAndValidity({ emitEvent: false });

    const start = this.filters.controls.initialDate.value as Date | null;
    const end = this.filters.controls.endDate.value as Date | null;

    if (!start || !end) return;

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    if (this.filters.invalid) return;

    const params = this.buildParams();

    this.isLoaded = false;

    this.betSummaryService
      .exportSummaryBetPdf(params)
      .pipe(
        finalize(() => {
          this.isLoaded = true;
        })
      )
      .subscribe({
        next: (value) => {
          const byteCharacters = atob(value);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: 'application/pdf',
          });

          const startName = this.ymd(this.asDate(start));
          const endName = this.ymd(this.asDate(end));

          saveAs(blob, `resumo_apostas_${startName}_${endName}.pdf`);
        },
        error: () => {
          this.toastr.error('Erro ao exportar PDF.', 'Erro', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
        },
      });
  }
}